import { getStoreLocations } from '//www.99bikes.co.nz/cdn/shop/t/819/assets/99-geolocation.js?manual=20260703&v=136991957648899336471786409090';

/**
 * @file: assets/store-locator-index.js
 * @purpose: Provides functionality for the store locator feature
 * @date: 2023-12-01
 */

const selectors = {
  radioInputs: '[data-state]',
  region: '[data-region]',
  storeList: '[data-store-list]',
};

class StoreLocatorIndex extends HTMLElement {
  constructor() {
    super();

    this.handleNavItemClick = this.handleNavItemClick.bind(this);
    this.init();
  }

  init() {
    // Cache DOM elements
    this.elements = {
      regionTitle: this.querySelector(selectors.region),
      storeList: this.querySelector(selectors.storeList),
    };

    this.attachEventListeners();
    this.loadInitialStores();
  }

  async loadInitialStores() {
    // Fetch initial store data
    const stores = await getStoreLocations(true);
    let storeOptions = this.dataset.storeLocations;

    try {
      storeOptions = JSON.parse(storeOptions);
    } catch (error) {
      console.error('Error parsing store options', error);
      return;
    }

    const orderedStores = {};
    storeOptions.forEach(option => {
      orderedStores[option] = [];
    });

    stores.forEach(store => {
      if (store.area_location && orderedStores[store.area_location]) {
        orderedStores[store.area_location].push(store);
      }
    });

    this.stores = orderedStores;

    const anchorValue = decodeURIComponent(window.location.hash.substring(1));

    if (anchorValue) {
      const selector = `[data-anchor="${anchorValue}"]`;
      const selectedStore = this.querySelector(selector);
      selectedStore.click();
    } else {
      this.populateStoreList(storeOptions[0]);
    }
  }

  attachEventListeners() {
    const radioInputs = this.querySelectorAll(selectors.radioInputs);
    if (!radioInputs) return;

    radioInputs.forEach(radioInput => {
      radioInput.addEventListener('change', this.handleNavItemClick);
    });
  }

  handleNavItemClick(event) {
    const { state, title, anchor } = event.target.dataset;

    if (this.elements.regionTitle) {
      this.elements.regionTitle.textContent = title;
    }

    // Set anchor
    const url = new URL(window.location);
    url.hash = anchor ? `#${anchor}` : '';
    history.replaceState({}, '', url);

    this.populateStoreList(state);
  }

  populateStoreList(state) {
    const storeList = this.elements.storeList;

    if (!storeList) return;

    const stores = this.stores[state].sort((a, b) => a.name.localeCompare(b.name));

    storeList.innerHTML = stores.length ? '' : 'No stores in this region';

    stores.forEach(store => {
      const storeCard = document.createElement('div');
      let todaysHours = '';

      //99B 20250905 -PO, added conditions for closed and opening soon
      if(store.todays_hours) {
        try {
          const todaysHourJSON = JSON.parse(store.todays_hours);
          const todaysTradingTimes = todaysHourJSON.hours.time;

          if (todaysTradingTimes.includes('Closed')) {
            todaysHours = 'Closed Today';
          } else if (todaysTradingTimes.includes('Opening Soon')) {
            todaysHours = '<b>Opening Soon!</b>'
          } else if (todaysTradingTimes) {
            todaysHours = `<b>Open Today:</b> ${todaysTradingTimes}`;
          }
        } catch (error) {
          console.error('Error parsing todays_hours', error);
        }
      }

      const storeName = `99 Bikes ${store.name.replace('Bike Shop', '')}`;

      storeCard.classList.add('store-index__store-card');
      storeCard.innerHTML = `
        <div class='store-index__store-card-content'>
          <h5 class='h5'> ${storeName} </h5>
          ${todaysHours ? `<p>${todaysHours}</p>` : ''}
          <p>
            <a href='https://www.google.com/maps/search/?api=1&query=${store.address}'>
              ${store.address}
            </a>
          </p>
          ${
            store.phone
              ? `<p>
            <a href='tel:${store.phone}'>
              ${store.phone}
            </a>
          </p>`
              : ''
          }
        </div>
        <div class='store-index__store-actions'>
          <a href='/pages/store/${store.handle}' class='store-index__link'> View Store Details </a>
        </div>
      `;
      storeList.appendChild(storeCard);
    });
  }

  disconnectedCallback() {
    const radioInputs = this.querySelectorAll(selectors.radioInputs);
    if (!radioInputs) return;

    radioInputs.forEach(radioInput => {
      radioInput.removeEventListener('change', this.handleNavItemClick);
    });
  }
}

// Define the custom element
customElements.define('store-locator-index', StoreLocatorIndex);
